import { useEffect, useRef } from "react";

const WHATSAPP_URL = "https://wa.me/5512991220961?text=Olá,%20gostaria%20de%20falar%20com%20um%20advogado.";

export default function HeroSection() {
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = titleRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSobre = () => {
    const el = document.querySelector("#sobre");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: "linear-gradient(135deg, #2A2A2A 0%, #3a2228 50%, #9A3D4A 100%)" }}
    >
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #B04756 0%, transparent 50%), radial-gradient(circle at 80% 20%, #C76A76 0%, transparent 40%)" }} />

      {/* Content */}
      <div ref={titleRef} className="relative z-10 w-full max-w-5xl mx-auto px-6 lg:px-12 text-center pt-24">
        {/* Eyebrow */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-100 inline-flex items-center gap-2 mb-8">
          <div className="h-px w-8 bg-rose-light" />
          <span className="text-rose-light text-xs font-medium uppercase tracking-widest">
            Escritório
          </span>
          <div className="h-px w-8 bg-rose-light" />
        </div>

        {/* H1 */}
        <h1 className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200 font-heading text-4xl lg:text-6xl xl:text-7xl text-white font-semibold leading-tight mb-6">
          Soluções jurídicas<br />
          <em className="text-rose-light not-italic">estratégicas</em> para<br />
          empresas e pessoas físicas
        </h1>

        {/* Subtitle */}
        <p className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-300 text-white/75 text-lg lg:text-xl font-light max-w-2xl mx-auto leading-relaxed mb-12">
          Atendimento Previdenciário, Trabalhista e Cível para Empresas e Pessoas Físicas de todo o Brasil.
        </p>

        {/* CTAs */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-500 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-rose-primary hover:bg-rose-dark text-white font-medium px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-base" />
            </div>
            Fale com um Advogado
          </a>
          <button
            onClick={scrollToSobre}
            className="flex items-center gap-2 border border-white/40 hover:border-white text-white font-medium px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm hover:bg-white/10"
          >
            Conheça o Escritório
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-down-line text-sm" />
            </div>
          </button>
        </div>

        {/* Stats */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-700 mt-20 grid grid-cols-3 gap-6 max-w-2xl mx-auto border-t border-white/20 pt-10">
          {[
            { value: "Empresas", label: "Jurídico Preventivo" },
            { value: "Pessoas", label: "Atendimento Previdenciário" },
            { value: "100%", label: "Foco em Resultados" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-white font-heading text-xl font-semibold mb-1">{stat.value}</div>
              <div className="text-white/50 text-xs">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Scroll indicator – mobile only (inline, below stats) */}
        <div
          className="flex md:hidden flex-col items-center gap-2 mt-10 animate-bounce cursor-pointer"
          onClick={scrollToSobre}
        >
          <div className="w-6 h-9 rounded-full border-2 border-white/30 flex items-start justify-center pt-1.5">
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </div>
        </div>
      </div>

      {/* Scroll indicator – desktop only (absolute bottom) */}
      <div className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 animate-bounce cursor-pointer" onClick={scrollToSobre}>
        <div className="w-6 h-9 rounded-full border-2 border-white/30 flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>
  );
}
